// #region imports

// --- Angular
import { Injectable } from '@angular/core';

// --- RxJs
import { BehaviorSubject, Observable, filter, map, of, take } from 'rxjs';

// --- 3rd Party
import _ from 'lodash';

// --- HS Code
import {
  RespondentEmailValidationModel,
  RespondentModel,
  RespondentValidationResultModel,
} from '../../../models';
import { ParticipantGroupsBaseService } from './participant-groups-base.service';

// #endregion

@Injectable({
  providedIn: 'root',
})
export abstract class RespondentsBaseService {
  protected _respondents: RespondentModel[] = new Array<RespondentModel>();

  protected _respondents$: BehaviorSubject<RespondentModel>[];
  get respondents$(): Observable<RespondentModel>[] {
    return this._respondents$;
  }

  protected _respondentBounceCheck$: BehaviorSubject<RespondentEmailValidationModel  | null>;
  get respondentBounceCheck$(): Observable<RespondentEmailValidationModel | null>  {
    return this._respondentBounceCheck$;
  }

  protected _previousRespondents$: BehaviorSubject<RespondentModel[]>;
  get previousRespondents$(): Observable<RespondentModel[]> {
    return this._previousRespondents$;
  }

  protected _respondentValidationResult$: BehaviorSubject<RespondentEmailValidationModel | null>;
  get respondentValidationResult$(): Observable<RespondentEmailValidationModel | null> {
    return this._respondentValidationResult$.asObservable();
  }

  constructor(protected groupsService: ParticipantGroupsBaseService) {
    this._respondentBounceCheck$ = new BehaviorSubject<RespondentEmailValidationModel | null>(null);
    this._respondents$ = new Array<BehaviorSubject<RespondentModel>>(15);
    this._previousRespondents$ = new BehaviorSubject<RespondentModel[]>(new Array<RespondentModel>());
    this._respondentValidationResult$ = new BehaviorSubject<RespondentEmailValidationModel | null>(null);

    for (let i = 0; i < 15; i++) {
      this._respondents$[i] = new BehaviorSubject<RespondentModel>({} as RespondentModel);
    }
  }

  abstract addOrUpdate(model: RespondentModel): void;
  abstract checkForRespondentEmailBounce(model: RespondentEmailValidationModel): void;
  abstract clearValidateRespondentEmailAddressV2(): void;
  abstract delete(respondent: RespondentModel): void;
  abstract getPreviousRespondents(pageSize?: number): void;
  abstract validateRespondentEmailAddress(model: RespondentEmailValidationModel): void;
  abstract validateRespondentEmailAddressV2(model: RespondentEmailValidationModel): void;

  protected _setRespondent(groupIndex: number, model: RespondentModel) {
    const respondentIndex = (groupIndex - 1) + (model.groupIndex - 1);
    const theRespondent = _.cloneDeep(this._respondents$[respondentIndex].value);
    theRespondent.isValid = model.isValid;
    theRespondent.emailAddress = model.emailAddress;
    theRespondent.errorMessage = model.errorMessage;
    this._respondents$[respondentIndex].next(theRespondent);
  }

  expandToggled(model: RespondentModel) {
    model.isExpanded = !model.isExpanded;
    // make sure the expanded respondent belongs to the activeGroup
    if (model.isExpanded) {
      this.groupsService.setActiveGroup(model);
    }
    this.groupsService.refreshRespondent(model);
  }

  resetRespondentBounceCheck() {
    this._respondentBounceCheck$.next(null);
  }

  protected _createEmptyRespondent(): RespondentModel {
    return {
      id: null,
      participantId: null,
      groupId: '',
      groupIndex: 1,
      firstName: '',
      lastName: '',
      emailAddress: '',
      avatar: '',
      percentageComplete: 0,
      status: 0,
      locale: '',
      languageId: 0,
      isValid: false,
      isSet: false,
      errorMessage: '',
      personalMessage: '',
      isValidatingEmailAddress: false,
      isSaving: false,
      isSendingReminder: false,
      isExpanded: false,
      lastEmailSentDate: null,
      invitationType: 0,
      canResendEmail: false
    } as RespondentModel;
  }
}
